// SpotifyPlayer.js - Combined Enhanced Version
import { useEffect, useRef, useCallback, useState } from 'react';
import { usePlayerContext } from '../../hooks/PlayerContext';
import logger from '../../services/logger';

const PLAYER_NAME = 'Phantom Music Player';

const SpotifyPlayer = () => {
  const {
    state,
    getValidToken,
    makeAuthenticatedRequest,
    setDeviceId,
    setPlaybackState,
    setProgress,
    setPlayerInstance,
    handleNextTrack
  } = usePlayerContext();

  const {
    currentTrackIndex,
    playlist,
    deviceId,
    isPlaying
  } = state;

  // Refs for state tracking and cleanup
  const playerRef = useRef(null);
  const lastPlayedTrackRef = useRef({ index: null, uri: null, timestamp: 0 });
  const trackEndTimeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);

  // Always-current ref for deviceId — prevents stale closure in async playTrack callbacks
  const deviceIdRef = useRef(null);
  deviceIdRef.current = deviceId;

  // Initialize global flags if not present
  useEffect(() => {
    if (typeof window !== 'undefined' && !window._playerStateFlags) {
      window._playerStateFlags = {
        isChangingTrack: false,
        isSeeking: false,
        trackEndProcessed: false
      };
    }
  }, []);

  // Helper function to debounce frequent calls
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  /**
   * Enhanced play track with centralized token management
   */
  const playTrack = useCallback(async (trackUri) => {
    try {
      // Always read from ref — never from closed-over state value
      const currentDeviceId = deviceIdRef.current;
      if (!currentDeviceId) {
        logger.warn('⚠️ No device ID available');
        return;
      }

      logger.info(`🎵 Playing track: ${trackUri}`);

      const response = await makeAuthenticatedRequest(
        `https://api.spotify.com/v1/me/player/play?device_id=${currentDeviceId}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            uris: [trackUri],
            position_ms: 0
          })
        },
        'spotify'
      );

      if (response.ok) {
        logger.success('✅ Track play successful');

        // Reset track end processing flag
        if (window._playerStateFlags) {
          window._playerStateFlags.trackEndProcessed = false;
        }
      } else {
        if (response.status === 404) {
          logger.error('❌ Device not found - forcing player reconnection');
          setPlayerReady(false);
        } else {
          logger.error('❌ Failed to play track');
        }
      }

    } catch (error) {
      logger.error('❌ Error playing track:', error);
    }
  }, [makeAuthenticatedRequest]); // deviceId intentionally omitted — read from deviceIdRef.current at call time

  /**
   * Enhanced pause with token management
   */
  const pausePlayback = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest(
        'https://api.spotify.com/v1/me/player/pause',
        { method: 'PUT' },
        'spotify'
      );

      if (response.ok) {
        logger.success('✅ Playback paused');
      }
    } catch (error) {
      logger.error('❌ Error pausing playback:', error);
    }
  }, [makeAuthenticatedRequest]);

  /**
   * Enhanced seek with token management
   */
  const seekToPosition = useCallback(async (positionMs) => {
    try {
      // Set seeking flag to prevent progress updates during seek
      if (window._playerStateFlags) {
        window._playerStateFlags.isSeeking = true;
      }

      const response = await makeAuthenticatedRequest(
        `https://api.spotify.com/v1/me/player/seek?position_ms=${positionMs}`,
        { method: 'PUT' },
        'spotify'
      );

      if (response.ok) {
        logger.debug(`🎯 Seeked to position: ${positionMs}ms`);
      }

      // Reset seeking flag after a delay
      setTimeout(() => {
        if (window._playerStateFlags) {
          window._playerStateFlags.isSeeking = false;
        }
      }, 1000);

    } catch (error) {
      logger.error('❌ Error seeking:', error);
      // Reset seeking flag on error
      if (window._playerStateFlags) {
        window._playerStateFlags.isSeeking = false;
      }
    }
  }, [makeAuthenticatedRequest]);

  /**
   * Set up progress polling for accurate UI updates
   * Note: Auto-advance handled by SpotifyMusicPlayer.js to prevent dual-system conflict
   */
  const setupProgressPolling = useCallback((player) => {
    // Clean up any existing intervals
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    if (!player) return;

    // Set up regular polling for track position
    progressIntervalRef.current = setInterval(async () => {
      try {
        const playerState = await player.getCurrentState();

        if (!playerState || playerState.paused) return;

        const position = playerState.position;
        const duration = playerState.duration;

        // Update progress for UI (only if not seeking)
        if (!window._playerStateFlags?.isSeeking) {
          setProgress(position, duration);
        }

        // Note: Auto-advance logic removed to prevent dual-system conflict
        // SpotifyMusicPlayer.js (lines 157-214) handles all auto-advance logic
        // This polling loop only updates progress for accurate UI display
      } catch (error) {
        logger.warn("⚠️ Error in track end detection:", error);
      }
    }, 250); // Check every 250ms

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [setProgress]);

  // Helper functions for player initialization
  const validateExistingPlayer = async () => {
    if (!window._spotifyPlayerInstance) return false;

    logger.info('🎵 Using existing Spotify player instance');
    playerRef.current = window._spotifyPlayerInstance;

    try {
      const state = await window._spotifyPlayerInstance.getCurrentState();
      if (state !== null) {
        setPlayerReady(true);
        setupProgressPolling(window._spotifyPlayerInstance);
        return true;
      } else {
        logger.warn('⚠️ Existing player has no state, reinitializing...');
        await cleanupExistingPlayer();
        return false;
      }
    } catch (error) {
      logger.warn('⚠️ Error checking existing player, reinitializing...');
      await cleanupExistingPlayer();
      return false;
    }
  };

  const cleanupExistingPlayer = async () => {
    try {
      if (window._spotifyPlayerInstance) {
        await window._spotifyPlayerInstance.disconnect();
      }
    } catch (e) { /* ignore */ }
    delete window._spotifyPlayerInstance;
  };

  const validateSpotifySDK = () => {
    if (!window.Spotify || !window.Spotify.Player) {
      logger.error('❌ Spotify SDK not fully loaded');
      setTimeout(initializePlayer, 500);
      return false;
    }
    return true;
  };

  const createSpotifyPlayer = async () => {
    return new window.Spotify.Player({
      name: PLAYER_NAME,
      getOAuthToken: async (cb) => {
        // Fetch token from Supabase
        const token = await getValidToken('spotify');
        cb(token);
      },
      volume: 0.7
    });
  };

  const setupPlayerErrorHandlers = (player) => {
    player.addListener('initialization_error', ({ message }) => {
      logger.error('❌ Spotify player initialization error:', message);
    });

    player.addListener('authentication_error', async ({ message }) => {
      logger.error('❌ Spotify player authentication error:', message);
      await getValidToken('spotify');
    });

    player.addListener('account_error', ({ message }) => {
      logger.error('❌ Spotify player account error:', message);
    });

    player.addListener('playback_error', ({ message }) => {
      logger.error('❌ Spotify player playback error:', message);
    });
  };

  const setupPlayerEventHandlers = (player) => {
    player.addListener('ready', ({ device_id }) => {
      logger.success(`✅ Spotify player ready with device ID: ${device_id}`);
      setDeviceId(device_id);
      setPlayerInstance(player);

      // CRITICAL: Wait for Spotify backend to register device before allowing playback
      // The SDK fires 'ready' locally, but Spotify's API needs time to register the device
      setTimeout(() => {
        setPlayerReady(true);
        logger.debug('🎯 Player ready flag set - allowing playback');
      }, 1500);

      setupProgressPolling(player);
    });

    player.addListener('not_ready', ({ device_id }) => {
      logger.warn(`⚠️ [PLAYER] Device went offline: ${device_id} — reconnecting`);
      setPlayerReady(false);
      // Clear last-played tracking so the current track re-plays automatically when the player reconnects
      lastPlayedTrackRef.current = { index: null, uri: null, timestamp: 0 };
      initializePlayer();
    });

    const handlePlayerStateChange = debounce((playerState) => {
      if (!playerState) {
        logger.debug('Received null playerState');
        return;
      }

      try {
        // Only update progress, NOT playback state
        // UI button stays in play state until user clicks it or tracks end
        if (!window._playerStateFlags?.isSeeking) {
          setProgress(playerState.position, playerState.duration);
        }
      } catch (error) {
        logger.error('❌ Error processing player state:', error);
      }
    }, 500);

    player.addListener('player_state_changed', handlePlayerStateChange);
  };

  const connectPlayer = async (player) => {
    const connected = await player.connect();
    if (connected) {
      logger.success('✅ Spotify player connected');
      playerRef.current = player;
      window._spotifyPlayerInstance = player;
      return true;
    } else {
      logger.error('❌ Failed to connect Spotify player');
      return false;
    }
  };

  /**
   * Initialize player with enhanced token management
   */
  const initializePlayer = useCallback(async () => {
    try {
      const freshToken = await getValidToken('spotify');

      if (!freshToken) {
        logger.error('❌ No valid Spotify token for player initialization');
        return;
      }

      // Check existing player first
      const isExistingPlayerValid = await validateExistingPlayer();
      if (isExistingPlayerValid) return;

      // Validate SDK availability
      if (!validateSpotifySDK()) return;

      logger.info('🎵 Initializing new Spotify player...');

      // Create and configure new player
      const player = await createSpotifyPlayer();
      setupPlayerErrorHandlers(player);
      setupPlayerEventHandlers(player);

      // Connect player
      await connectPlayer(player);

    } catch (error) {
      logger.error('❌ Error initializing Spotify player:', error);
    }
  }, [getValidToken, setDeviceId, setPlayerInstance, setPlaybackState, setProgress, setupProgressPolling, isPlaying]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load Spotify SDK and initialize player
  useEffect(() => {
    const loadSpotifySDK = () => {
      if (document.querySelector('script[src="https://sdk.scdn.co/spotify-player.js"]')) {
        if (window.Spotify) {
          initializePlayer();
        }
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      document.body.appendChild(script);

      window.onSpotifyWebPlaybackSDKReady = initializePlayer;
    };

    if (getValidToken) {
      if (window.Spotify) {
        initializePlayer();
      } else {
        loadSpotifySDK();
      }
    }
  }, [initializePlayer, getValidToken]);

  // FIX: Extract current track URI to prevent unnecessary effect triggers
  // Only depend on the URI of the current track, not the entire playlist array
  const currentTrackUri = playlist.tracks?.[currentTrackIndex]?.uri;

  // Handle track changes with robust duplicate prevention
  useEffect(() => {
    if (!deviceId || !playlist.tracks || playlist.tracks.length === 0 || !playerReady) {
      return;
    }

    const currentTrack = playlist.tracks[currentTrackIndex];
    if (!currentTrack?.uri) {
      logger.error('❌ No valid track found at index', currentTrackIndex);
      return;
    }

    // FIX: Prevent duplicate track changes - check URI only (index can change during reorder)
    if (lastPlayedTrackRef.current.uri === currentTrack.uri) {
      // Same track is already playing (regardless of index)
      // This handles reordering where index changes but track stays the same
      logger.debug('Skipping play - same track already playing:', currentTrack.name);

      // Update index tracking for future checks
      lastPlayedTrackRef.current.index = currentTrackIndex;
      return;
    }

    // Update tracking before making the request
    const now = Date.now();
    lastPlayedTrackRef.current = {
      index: currentTrackIndex,
      uri: currentTrack.uri,
      timestamp: now
    };

    // Set changing track flag
    if (window._playerStateFlags) {
      window._playerStateFlags.isChangingTrack = true;
    }

    // Small delay to prevent race conditions
    setTimeout(() => {
      playTrack(currentTrack.uri).finally(() => {
        // Reset flag after completion
        setTimeout(() => {
          if (window._playerStateFlags) {
            window._playerStateFlags.isChangingTrack = false;
          }
        }, 500);
      });
    }, 300);

  }, [deviceId, currentTrackIndex, currentTrackUri, playerReady, playTrack]);

  // Expose methods for external use
  useEffect(() => {
    window.spotifyPlayerControls = {
      play: playTrack,
      pause: pausePlayback,
      seek: seekToPosition
    };
  }, [playTrack, pausePlayback, seekToPosition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (trackEndTimeoutRef.current) {
        clearTimeout(trackEndTimeoutRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  return null;
};

export default SpotifyPlayer;
